import React, { useState } from "react";
import { Link } from "react-router-dom";
import BookDemoModal from "../components/BookDemoModal";
import {
  Brain,
  Target,
  Zap,
  BarChart3,
  Users,
  CheckCircle,
  ArrowRight,
  Lightbulb,
  Shield,
  Video,
  FileText,
  Award,
  GraduationCap,
  TrendingUp,
  Sparkles,
} from "lucide-react";

const Features = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const coreFeatures = [
    {
      icon: Brain,
      title: "Automated Test Creation",
      description:
        "Upload job descriptions and automatically generate customized technical tests in minutes, not hours.",
      benefits: [
        "Instant test creation from job descriptions",
        "Adaptive difficulty levels",
        "Role-specific assessments",
      ],
    },
    {
      icon: Video,
      title: "Intelligent Interview System",
      description:
        "Conversational intelligent interviewer that adapts to candidate responses and provides comprehensive evaluation.",
      benefits: [
        "Adaptive questioning based on answers",
        "24/7 availability",
        "Consistent, unbiased evaluation",
      ],
    },
    {
      icon: Shield,
      title: "Advanced Proctoring",
      description:
        "10-layer security system including camera monitoring, tab detection, and real-time violation tracking.",
      benefits: [
        "Camera & microphone monitoring",
        "Tab switching detection",
        "Copy/paste blocking",
      ],
    },
    {
      icon: FileText,
      title: "Resume Parsing & Validation",
      description:
        "Intelligent resume analysis that extracts skills and generates validation tests based on candidate claims.",
      benefits: [
        "Automatic skill extraction",
        "Resume-based test generation",
        "Skills verification badges",
      ],
    },
    {
      icon: Award,
      title: "Verification Badge System",
      description:
        "Candidates earn verified badges after passing skills validation tests, proving their capabilities.",
      benefits: [
        "Green checkmark verification",
        "Skills-based validation",
        "Increased candidate credibility",
      ],
    },
    {
      icon: BarChart3,
      title: "Analytics & Insights",
      description:
        "Comprehensive dashboards with candidate performance metrics, hiring analytics, and data-driven insights.",
      benefits: [
        "Real-time performance tracking",
        "Hiring pattern analysis",
        "Candidate comparison tools",
      ],
    },
  ];

  const jobSeekerFeatures = [
    {
      icon: GraduationCap,
      title: "Skills Validation",
      description: "Take tests based on your resume to verify your claimed skills and earn verification badges.",
    },
    {
      icon: TrendingUp,
      title: "Application Tracking",
      description: "Track all your job applications and test submissions in one centralized dashboard.",
    },
    {
      icon: Target,
      title: "Personalized Learning",
      description: "Get customized course recommendations based on your test results and career goals.",
    },
  ];

  const hrFeatures = [
    {
      icon: Users,
      title: "Automated Screening",
      description: "Screen hundreds of candidates efficiently with intelligent evaluation and ranking.",
    },
    {
      icon: Zap,
      title: "Candidate Management",
      description: "Manage your candidate pipeline with easy shortlist, reject, and save features.",
    },
    {
      icon: Brain,
      title: "Intelligent Recommendations",
      description: "Get intelligent hiring recommendations (STRONG RECOMMEND, RECOMMEND, etc.) for each candidate.",
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Modern Navigation */}
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-0 -ml-1 group">
            <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8 sm:h-9 sm:w-9" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent group-hover:from-red-800 group-hover:via-primary group-hover:to-red-800 transition-all duration-300">
              VetriaAI
            </span>
          </Link>
          <div className="flex items-center">
            <Link
              to="/login"
              className="bg-primary text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-sm sm:text-base shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-blue-50 opacity-50"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(185,28,28,0.1),transparent_50%)]"></div>

        <div className="relative max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-gray-200 mb-6 sm:mb-8">
            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
            <span className="text-xs sm:text-sm font-semibold text-gray-700">Platform Features</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-2">
            Powerful Features for
            <span className="block mt-1 sm:mt-2 bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent">
              Modern Recruitment
            </span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-4">
            Everything you need to streamline hiring, verify skills, and find the best talent faster.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link
              to="/login"
              className="group w-full sm:w-auto bg-primary text-white px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-primary-hover transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Get Started
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <button
              onClick={() => setIsModalOpen(true)}
              className="group w-full sm:w-auto border-2 border-gray-300 text-gray-700 px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center justify-center"
            >
              Book Your Demo
            </button>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-gray-50 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Core Features</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Everything You Need
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Comprehensive tools designed to transform your hiring process
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {coreFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group bg-white p-6 sm:p-8 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20 hover:-translate-y-2"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-primary/10 to-primary/5 rounded-card flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-3 sm:mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li
                        key={idx}
                        className="flex items-center text-xs sm:text-sm text-gray-600"
                      >
                        <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-success mr-2 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* For Job Seekers */}
      <section className="py-12 sm:py-16 md:py-24 bg-white px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">For Job Seekers</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Stand Out to Employers
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Prove your skills and get verified badges that increase your credibility
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {jobSeekerFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group bg-gradient-to-br from-white to-gray-50 p-6 sm:p-8 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/30 hover:-translate-y-2"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary/20 to-primary/10 rounded-card flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* For HR/Recruiters */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">For HR & Recruiters</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Hire Smarter, Faster
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Powerful tools to streamline your recruitment process and find the best candidates
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {hrFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group bg-white p-8 rounded-card shadow-card hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/30 hover:-translate-y-2"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/10 rounded-card flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 md:py-24 bg-white px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 rounded-full mb-3 sm:mb-4">
              <span className="text-xs sm:text-sm font-semibold text-primary">Simple Process</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              How It Works
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Simple, efficient, and powerful - see how easy it is to get started
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center group">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-card flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Lightbulb className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                1. Upload Job Description
              </h3>
              <p className="text-sm sm:text-base text-gray-600 px-2">
                Simply upload your job description, and our intelligent system will analyze the key skills needed.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-card flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Brain className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                2. Automated Test Creation
              </h3>
              <p className="text-sm sm:text-base text-gray-600 px-2">
                Our system automatically creates customized assessments tailored to your specific job requirements.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-card flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300">
                <Target className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                3. Get Qualified Candidates
              </h3>
              <p className="text-sm sm:text-base text-gray-600 px-2">
                Review detailed results and insights to make informed hiring decisions with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-red-800 to-red-900"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>

        <div className="relative max-w-4xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 px-2">
            Ready to Experience These Features?
          </h2>
          <p className="text-base sm:text-lg md:text-xl mb-8 sm:mb-10 opacity-95 max-w-2xl mx-auto px-4">
            Join early adopters using VetriaAI to transform their hiring process.
          </p>
          <Link
            to="/login"
            className="group bg-white text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-button hover:bg-gray-50 transition-all duration-300 font-semibold text-base sm:text-lg inline-flex items-center shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Get Started
            <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="md:col-span-1">
              <Link to="/" className="mb-4 inline-flex items-center gap-0 -ml-1">
                <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8" />
                <span className="text-2xl font-bold bg-gradient-to-r from-primary via-red-400 to-primary bg-clip-text text-transparent">
                  VetriaAI
                </span>
              </Link>
              <p className="text-gray-400 leading-relaxed">
                Revolutionizing recruitment with intelligent automation.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Product</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/features" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Company</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-white transition-colors duration-200">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Support</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/help-center" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Help Center
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-primary">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors duration-200">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 VetriaAI. All copyrights are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>
      <BookDemoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default Features;
